/**
 * CEFR Knowledge Points API Service
 * Handles API calls for CEFR knowledge points associated with courses
 */

import { apiClient as api } from './apiClient';

import { API_BASE_URL } from '../config';
const CEFR_API_BASE = `${API_BASE_URL}/api/v1/cefr-knowledge`;

// Types
export type CEFRLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
export type KnowledgePointCategory = 'vocabulary' | 'phrasal_verb' | 'grammar';

export interface KnowledgePoint {
  id: string;
  term: string;
  category: KnowledgePointCategory;
  cefrLevel: CEFRLevel;
  definitions: {
    en?: string;
    'zh-TW'?: string;
    'zh-CN'?: string;
  };
}

export interface CourseKnowledgePointStats {
  total: number;
  byCategory: {
    vocabulary: number;
    phrasal_verb: number;
    grammar: number;
  };
  byLevel: Record<CEFRLevel, number>;
  knowledgePoints: KnowledgePoint[];
}

// Annotation types for precise word highlighting
export interface WordAnnotation {
  wordText: string;
  baseForm: string;
  startPosition: number;
  endPosition: number;
  matchType: 'exact' | 'lemma';
  cefrLevel: CEFRLevel;
  knowledgePointId: string;
}

export interface CourseAnnotationsResponse {
  annotations: Record<string, WordAnnotation[]>; // segmentId -> annotations
  stats: {
    totalAnnotations: number;
    segmentsWithAnnotations: number;
  };
}

export interface CompleteCefrLevelMapResponse {
  cefrLevelMap: Record<string, CEFRLevel>;
  phrasalVerbMap: Record<string, CEFRLevel>;
  stats: {
    fromKnowledgePoints: number;
    fromContentScan: number;
    total: number;
  };
}

export const cefrKnowledgeService = {
  /**
   * Get knowledge points for a course with statistics
   */
  async getCourseKnowledgePointStats(courseId: string): Promise<CourseKnowledgePointStats> {
    const response = await api.get<CourseKnowledgePointStats>(
      `${CEFR_API_BASE}/course/${courseId}/stats`,
    );
    return response.data;
  },

  /**
   * Get all knowledge points for a course
   */
  async getCourseKnowledgePoints(courseId: string): Promise<KnowledgePoint[]> {
    const response = await api.get<KnowledgePoint[]>(
      `${CEFR_API_BASE}/course/${courseId}`,
    );
    return response.data;
  },

  /**
   * Search knowledge points by term
   */
  async searchKnowledgePoints(query: string, limit = 20): Promise<KnowledgePoint[]> {
    const response = await api.get<KnowledgePoint[]>(
      `${CEFR_API_BASE}/search`,
      { params: { q: query, limit } },
    );
    return response.data;
  },

  /**
   * Get knowledge points by level and category
   */
  async getByLevelAndCategory(
    level: CEFRLevel,
    category?: KnowledgePointCategory,
  ): Promise<KnowledgePoint[]> {
    const response = await api.get<KnowledgePoint[]>(
      CEFR_API_BASE,
      { params: { level, category } },
    );
    return response.data;
  },

  /**
   * Get all CEFR annotations for a course's segments
   * Returns annotations organized by segment ID with exact positions
   */
  async getCourseAnnotations(courseId: string): Promise<CourseAnnotationsResponse> {
    const response = await api.get<CourseAnnotationsResponse>(
      `${CEFR_API_BASE}/course/${courseId}/annotations`,
    );
    return response.data;
  },

  /**
   * Get complete CEFR level map for a course
   * Combines pre-tagged knowledge points with dynamically scanned content
   */
  async getCompleteCefrLevelMap(courseId: string): Promise<CompleteCefrLevelMapResponse> {
    const response = await api.get<CompleteCefrLevelMapResponse>(
      `${CEFR_API_BASE}/course/${courseId}/level-map`,
    );
    return response.data;
  },
};

export default cefrKnowledgeService;
